import { Link } from 'react-router';

export function TermsPage() {
  return (
    <div className="min-h-screen bg-base-100">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 lg:px-12 py-4 border-b border-base-300/30">
        <Link to="/">
          <div className="text-2xl font-extrabold tracking-tight">
            Hy<span className="text-primary">Pe</span>Ma
          </div>
        </Link>
        <Link to="/login" className="btn btn-ghost btn-sm text-base-content/60">
          Login
        </Link>
      </nav>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 lg:px-12 py-16">
        <h1 className="text-3xl font-bold tracking-tight mb-2">Terms of Service</h1>
        <p className="text-sm text-base-content/40 mb-10">Last updated: April 2026</p>

        <div className="space-y-10 text-base-content/70 text-sm leading-relaxed">

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">1. Scope</h2>
            <p className="mb-3">
              These Terms of Service (&quot;Terms&quot;) govern the use of the HyPeMa platform,
              accessible at{' '}
              <a href="https://hypema.app" className="text-primary hover:underline">hypema.app</a>,
              including all related services, APIs, and the HyPeMa Chrome extension
              (&quot;HyPeMa &mdash; Gmail Draft Creator&quot;), collectively referred to as the
              &quot;Service&quot;.
            </p>
            <p className="mb-3">
              The Service is operated by OXO UG (haftungsbeschr&auml;nkt), R&uuml;benackerweg 2,
              82418 Seehausen am Staffelsee, Germany, represented by its Managing Director
              Robert Stammen (&quot;OXO UG&quot;, &quot;we&quot;, &quot;our&quot;, &quot;us&quot;).
            </p>
            <p className="mb-3">
              HyPeMa is a business-to-business (B2B) service. By registering for an account or
              using the Service, you confirm that you are acting in a professional or commercial
              capacity and not as a consumer. These Terms form a binding agreement between you
              (&quot;User&quot;, &quot;you&quot;) and OXO UG.
            </p>
            <p>
              Any deviating, conflicting, or supplementary general terms and conditions of the
              User shall not become part of this agreement, even if we do not expressly object
              to them.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">2. Service Description</h2>
            <p className="mb-3">
              HyPeMa is an AI-powered SaaS platform for hyper-personalized cold email outreach.
              The Service enables users to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                <strong>Find and manage leads</strong> &mdash; search for and organize business
                contacts and companies for outreach campaigns.
              </li>
              <li>
                <strong>AI-powered email personalization</strong> &mdash; generate personalized
                email drafts using artificial intelligence based on lead and company data.
              </li>
              <li>
                <strong>Company research</strong> &mdash; AI-assisted research on target companies
                to inform outreach strategies.
              </li>
              <li>
                <strong>Chrome extension for Gmail draft creation</strong> &mdash; the HyPeMa
                Chrome extension creates email drafts directly in the User&apos;s Gmail account.
                Drafts are created locally; the User retains full control over reviewing and
                sending each email.
              </li>
            </ul>
            <p>
              The Service is hosted on Supabase infrastructure located in the EU (Frankfurt,
              Germany). We reserve the right to modify, improve, or discontinue features of the
              Service at any time, provided that the core functionality remains substantially
              intact during any active subscription period.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">3. Account Registration</h2>
            <p className="mb-3">
              Access to the Service requires registration via Google OAuth. By registering, you
              agree to the following:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                You must provide accurate and complete information during the registration process.
              </li>
              <li>
                Each natural person may maintain only one account. Duplicate accounts may be
                terminated without notice.
              </li>
              <li>
                You are solely responsible for maintaining the security of your account and for
                all activities that occur under your account.
              </li>
              <li>
                You must notify us immediately at{' '}
                <a href="mailto:service@newstelligent.com" className="text-primary hover:underline">
                  service@newstelligent.com
                </a>{' '}
                if you become aware of any unauthorized use of your account.
              </li>
              <li>
                We reserve the right to suspend or terminate accounts that violate these Terms or
                that show signs of fraudulent or unauthorized activity.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">4. Subscription and Payment</h2>
            <p className="mb-3">
              HyPeMa operates on a token-based billing model with the following tiers:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                <strong>Free tier</strong> &mdash; 500 tokens per month at no charge.
              </li>
              <li>
                <strong>Paid plans</strong> &mdash; additional token allowances are available
                through paid subscription plans as displayed on the Service&apos;s pricing page.
              </li>
            </ul>

            <h3 className="font-medium text-base-content mb-2">Billing cycles</h3>
            <p className="mb-4">
              Subscriptions are billed on either a monthly or annual cycle, as selected by the
              User at the time of purchase. Annual subscriptions are billed as a single upfront
              payment for the full year.
            </p>

            <h3 className="font-medium text-base-content mb-2">Payment</h3>
            <p className="mb-4">
              All payments are processed through our designated payment provider. By subscribing
              to a paid plan, you authorize recurring charges according to the selected billing
              cycle. All prices are displayed exclusive of applicable taxes unless stated otherwise.
            </p>

            <h3 className="font-medium text-base-content mb-2">Invoicing</h3>
            <p>
              Invoices are provided electronically only. By accepting these Terms, you consent to
              receiving invoices via email or through your account dashboard.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">5. Token System</h2>
            <p className="mb-3">
              The Service uses a token-based system to meter AI-powered operations. Token costs
              vary by operation type:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                <strong>Company research</strong> &mdash; 5 to 15 tokens per operation, depending
                on scope and depth of analysis.
              </li>
              <li>
                <strong>Lead generation</strong> &mdash; 10 to 30 tokens per operation, depending
                on the number of leads and data enrichment level.
              </li>
              <li>
                <strong>Email generation</strong> &mdash; 2 to 5 tokens per email, depending on
                personalization complexity.
              </li>
            </ul>
            <p className="mb-3">
              Managing leads, configuring settings, and other non-AI operations are free of charge
              and do not consume tokens.
            </p>
            <p className="mb-3">
              Token costs may be adjusted from time to time. We will notify Users of significant
              changes to token pricing at least 14 days in advance.
            </p>
            <p>
              <strong>Unused tokens do not roll over.</strong> Any tokens remaining at the end of
              a billing cycle expire and are not carried forward to the next cycle, refunded, or
              otherwise compensated.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">6. Acceptable Use</h2>
            <p className="mb-3">
              You agree to use the Service only for lawful purposes and in compliance with all
              applicable laws and regulations. The following uses are strictly prohibited:
            </p>

            <h3 className="font-medium text-base-content mb-2">Spam and unsolicited communications</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                Sending bulk unsolicited emails or any form of spam.
              </li>
              <li>
                Using the Service to send messages to recipients who have not consented to receive
                communications or where no legitimate basis for contact exists.
              </li>
              <li>
                Failing to honor unsubscribe requests promptly.
              </li>
            </ul>

            <h3 className="font-medium text-base-content mb-2">Regulatory compliance</h3>
            <p className="mb-4">
              <strong>The User is solely responsible</strong> for ensuring that their use of the
              Service complies with all applicable data protection and marketing laws, including
              but not limited to the EU General Data Protection Regulation (GDPR), the US
              CAN-SPAM Act, the Canadian Anti-Spam Legislation (CASL), and any other local or
              international regulations governing electronic communications. HyPeMa provides
              tools for outreach but does not guarantee legal compliance on behalf of the User.
            </p>

            <h3 className="font-medium text-base-content mb-2">Prohibited content and conduct</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                Creating, distributing, or facilitating illegal, harassing, defamatory, threatening,
                or discriminatory content.
              </li>
              <li>
                Using AI features to generate harmful, misleading, deceptive, or fraudulent content.
              </li>
              <li>
                Impersonating another person or entity, or misrepresenting your affiliation with
                any person or organization.
              </li>
              <li>
                Attempting to reverse-engineer, decompile, or otherwise extract the source code
                of the Service.
              </li>
              <li>
                Interfering with or disrupting the integrity or performance of the Service or its
                underlying infrastructure.
              </li>
            </ul>

            <h3 className="font-medium text-base-content mb-2">User responsibility for AI-generated emails</h3>
            <p>
              The User is responsible for reviewing all AI-generated email content before sending.
              AI-generated drafts are suggestions and may contain inaccuracies. The User bears
              full responsibility for any email sent from their account, regardless of whether it
              was generated by AI.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">7. AI-Generated Content &amp; Data Processing by AI</h2>

            <h3 className="font-medium text-base-content mb-2">7.1 AI Technology Used</h3>
            <p className="mb-3">
              The Service uses Anthropic&apos;s Claude Opus 4 large language model (a generative AI system)
              to power company research, lead generation, and email content generation. In accordance with
              Regulation (EU) 2024/1689 (the EU AI Act), we disclose that all email drafts, company research
              summaries, and lead suggestions produced by HyPeMa are <strong>generated by artificial
              intelligence</strong> and may appear human-written but are machine-generated.
            </p>

            <h3 className="font-medium text-base-content mb-2">7.2 Data Sent to AI</h3>
            <p className="mb-2">
              When you use AI-powered features, the following data is transmitted to Anthropic for processing:
            </p>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li><strong>Company research:</strong> Your company website URL. The AI uses web search to gather publicly available information.</li>
              <li><strong>Lead generation:</strong> Your search criteria (industry, role, region) and company context.</li>
              <li><strong>Email generation:</strong> Lead data (name, company, role, country), your company profile, pitch/goal description, and tone preferences.</li>
            </ul>
            <p className="mb-3">
              Anthropic processes this data in the United States under the EU-U.S. Data Privacy Framework.
              Per Anthropic&apos;s API terms, your data is <strong>not used to train, improve, or fine-tune
              AI models</strong>. API inputs and outputs are retained by Anthropic for up to 30 days for
              trust &amp; safety purposes, then deleted.
            </p>

            <h3 className="font-medium text-base-content mb-2">7.3 Disclaimer &amp; Limitations</h3>
            <ul className="list-disc pl-6 space-y-2 mb-3">
              <li>
                <strong>No warranty of accuracy.</strong> AI-generated content may contain factual
                errors, hallucinations, outdated information, biased content, or inappropriate suggestions.
                We make no representation or warranty regarding the accuracy, completeness, or reliability
                of any AI-generated output.
              </li>
              <li>
                <strong>User review required.</strong> All AI-generated content must be reviewed,
                verified, and approved by the User before use. The User is solely responsible for
                the final content of any communication sent using the Service.
              </li>
              <li>
                <strong>Not professional advice.</strong> AI-generated content does not constitute
                legal, financial, business, or any other form of professional advice.
              </li>
              <li>
                <strong>Continuous improvement.</strong> AI models are updated periodically. Output
                quality and behavior may vary over time as models are refined.
              </li>
            </ul>

            <h3 className="font-medium text-base-content mb-2">7.4 Human Oversight</h3>
            <p>
              HyPeMa is designed with mandatory human-in-the-loop review. No AI-generated email is sent
              automatically. The Chrome extension creates drafts in the User&apos;s Gmail account; the User
              must manually review and send each email. The User bears full responsibility for any email
              sent from their account, regardless of whether it was generated by AI.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">8. Chrome Extension</h2>
            <p className="mb-3">
              The HyPeMa Chrome extension (&quot;HyPeMa &mdash; Gmail Draft Creator&quot;)
              provides the following functionality:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                The extension creates email drafts in the User&apos;s Gmail account by automating
                the Gmail compose window.
              </li>
              <li>
                Drafts are created locally in the User&apos;s Gmail account. The extension does
                not send any emails automatically.
              </li>
              <li>
                The User retains full control over reviewing, editing, and manually sending each
                draft.
              </li>
            </ul>
            <p className="mb-3">
              HyPeMa is not responsible for:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                The deliverability of emails sent by the User from their Gmail account.
              </li>
              <li>
                The standing or reputation of the User&apos;s Gmail account, including any
                restrictions, suspensions, or penalties imposed by Google.
              </li>
              <li>
                Any changes to Gmail&apos;s interface or functionality that may affect the
                extension&apos;s operation.
              </li>
            </ul>
          </section>

          <section id="dpa">
            <h2 className="text-lg font-semibold text-base-content mb-3">
              9. Data Processing Agreement (Auftragsverarbeitungsvertrag gem&auml;&szlig; Art.&nbsp;28 DSGVO)
            </h2>
            <p className="mb-3">
              This Data Processing Agreement (&quot;DPA&quot;) forms an integral part of these Terms and
              takes effect automatically upon registration for the Service. It governs the processing
              of personal data by OXO UG (&quot;Processor&quot;) on behalf of the User (&quot;Controller&quot;)
              pursuant to Art.&nbsp;28 of the General Data Protection Regulation (GDPR/DSGVO).
            </p>

            <h3 className="text-sm font-semibold text-base-content mt-6 mb-2">9.1 Subject Matter and Duration</h3>
            <p className="mb-3">
              The subject matter of this DPA is the operation, maintenance, and support of the HyPeMa
              platform for the purpose of AI-powered lead management and email generation. The duration
              of processing corresponds to the term of the User&apos;s account. Processing ceases upon
              account termination, subject to the deletion timelines set out in Section&nbsp;9.10.
            </p>

            <h3 className="text-sm font-semibold text-base-content mt-6 mb-2">9.2 Nature and Purpose of Processing</h3>
            <p className="mb-3">Processing activities include:</p>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li>Storage and management of lead/contact data</li>
              <li>AI-powered research and personalized email generation via third-party AI providers</li>
              <li>Management of unsubscribe requests and suppression lists</li>
              <li>Usage statistics and billing</li>
              <li>Audit logging for compliance and support purposes</li>
            </ul>

            <h3 className="text-sm font-semibold text-base-content mt-6 mb-2">9.3 Types of Personal Data</h3>
            <p className="mb-3">The following categories of personal data are processed:</p>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li>Lead/contact data: name, email address, company, job title, country, notes</li>
              <li>Generated email content (subject lines, email bodies)</li>
              <li>Unsubscribe tokens and suppression records (stored as salted hashes)</li>
              <li>Usage and audit data (API calls, token consumption, event logs)</li>
            </ul>

            <h3 className="text-sm font-semibold text-base-content mt-6 mb-2">9.4 Categories of Data Subjects</h3>
            <p className="mb-3">
              The data subjects are the leads, prospects, and business contacts whose data the User
              uploads to or generates through the Service.
            </p>

            <h3 className="text-sm font-semibold text-base-content mt-6 mb-2">9.5 Controller&apos;s Instructions</h3>
            <p className="mb-3">
              The Processor shall process personal data only on documented instructions from the
              Controller. The use of the Service (uploading leads, triggering email generation,
              managing campaigns) constitutes such instructions. The Processor shall immediately
              inform the Controller if, in its opinion, an instruction infringes applicable data
              protection law.
            </p>

            <h3 className="text-sm font-semibold text-base-content mt-6 mb-2">9.6 Confidentiality</h3>
            <p className="mb-3">
              The Processor ensures that all persons authorized to process personal data have
              committed themselves to confidentiality or are under an appropriate statutory
              obligation of confidentiality.
            </p>

            <h3 className="text-sm font-semibold text-base-content mt-6 mb-2">9.7 Technical and Organizational Measures (Art.&nbsp;32 GDPR)</h3>
            <p className="mb-3">
              The Processor implements appropriate technical and organizational measures to ensure
              a level of security appropriate to the risk, including but not limited to:
            </p>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li>Encryption of data at rest and in transit (TLS 1.2+)</li>
              <li>Row-Level Security (RLS) enforcing strict tenant isolation</li>
              <li>Salted hashing of email addresses in suppression lists</li>
              <li>Automatic deletion of generated email content after 7 days</li>
              <li>API keys stored as irreversible SHA-256 hashes</li>
              <li>Regular security updates and dependency auditing</li>
            </ul>
            <p className="mb-3">
              Further details on our security measures are described in our{' '}
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
            </p>

            <h3 className="text-sm font-semibold text-base-content mt-6 mb-2">9.8 Sub-processors</h3>
            <p className="mb-3">
              The Controller grants the Processor general authorization to engage the following
              sub-processors. The Processor ensures that sub-processors are bound by equivalent
              data protection obligations:
            </p>
            <div className="overflow-x-auto mb-3">
              <table className="table table-sm w-full">
                <thead>
                  <tr>
                    <th>Sub-processor</th>
                    <th>Purpose</th>
                    <th>Location</th>
                    <th>Safeguard</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Supabase Inc.</td>
                    <td>Database hosting, authentication</td>
                    <td>EU (Frankfurt)</td>
                    <td>DPA</td>
                  </tr>
                  <tr>
                    <td>Anthropic PBC</td>
                    <td>AI email generation</td>
                    <td>USA</td>
                    <td>DPA + EU SCCs</td>
                  </tr>
                  <tr>
                    <td>Polar.sh</td>
                    <td>Billing &amp; subscriptions</td>
                    <td>EU</td>
                    <td>DPA</td>
                  </tr>
                  <tr>
                    <td>Google LLC</td>
                    <td>OAuth authentication</td>
                    <td>USA</td>
                    <td>EU&ndash;US DPF</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mb-3">
              The Processor shall notify the Controller of any intended changes to the list of
              sub-processors at least 30 days in advance, providing the Controller with the
              opportunity to object. If the Controller objects on reasonable data protection grounds,
              the parties shall negotiate in good faith to find an alternative solution.
            </p>

            <h3 className="text-sm font-semibold text-base-content mt-6 mb-2">9.9 Data Subject Rights (Art.&nbsp;15&ndash;22 GDPR)</h3>
            <p className="mb-3">
              The Processor shall assist the Controller in fulfilling its obligations to respond
              to data subject requests (access, rectification, erasure, restriction, portability,
              objection). The Processor shall promptly forward any data subject request it receives
              directly to the Controller.
            </p>

            <h3 className="text-sm font-semibold text-base-content mt-6 mb-2">9.10 Deletion and Return of Data</h3>
            <p className="mb-3">
              Upon termination of the User&apos;s account, the Processor shall, at the Controller&apos;s
              choice, delete or return all personal data within 30 days. The User may export their
              data prior to termination through the account settings. Backup copies are deleted
              within 7 days after the primary data deletion. Data subject to statutory retention
              obligations (e.g., tax records) may be retained for up to 10 years.
            </p>

            <h3 className="text-sm font-semibold text-base-content mt-6 mb-2">9.11 Data Breach Notification</h3>
            <p className="mb-3">
              The Processor shall notify the Controller without undue delay, and in any event within
              48 hours, after becoming aware of a personal data breach. The notification shall include
              the nature of the breach, categories and approximate number of data subjects affected,
              likely consequences, and measures taken or proposed to mitigate the breach.
            </p>

            <h3 className="text-sm font-semibold text-base-content mt-6 mb-2">9.12 Audit Rights</h3>
            <p className="mb-3">
              The Processor shall make available to the Controller all information necessary to
              demonstrate compliance with Art.&nbsp;28 GDPR. The Processor shall provide an annual
              security summary report upon request. On-site audits shall only be conducted upon
              reasonable notice of at least 30 days, during normal business hours, and at the
              Controller&apos;s expense.
            </p>

            <h3 className="text-sm font-semibold text-base-content mt-6 mb-2">9.13 Assistance with DPIAs</h3>
            <p className="mb-3">
              The Processor shall assist the Controller with data protection impact assessments
              (Art.&nbsp;35 GDPR) and prior consultations with supervisory authorities (Art.&nbsp;36 GDPR)
              where the processing under this agreement is likely to result in a high risk to the
              rights and freedoms of natural persons.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">10. Intellectual Property</h2>
            <p className="mb-3">
              <strong>Platform ownership.</strong> The Service, including its software, design,
              trademarks, and all underlying technology, is and remains the exclusive property of
              OXO UG. Nothing in these Terms grants the User any rights to our intellectual
              property beyond the limited license to use the Service as described herein.
            </p>
            <p className="mb-3">
              <strong>User data ownership.</strong> The User retains all rights, title, and
              interest in their data, including lead information, campaign content, and any data
              uploaded to the Service.
            </p>
            <p>
              <strong>License to process.</strong> By using the Service, the User grants OXO UG a
              limited, non-exclusive license to process, store, and transmit User data solely for
              the purpose of providing and improving the Service. This license terminates upon
              account deletion.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">11. Liability</h2>
            <p className="mb-3">
              OXO UG&apos;s liability is governed as follows:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                <strong>Unlimited liability.</strong> We are liable without limitation for damages
                caused by intent or gross negligence, as well as for damages resulting from injury
                to life, body, or health.
              </li>
              <li>
                <strong>Limited liability for slight negligence.</strong> In cases of slight
                negligence, we are liable only for breaches of material contractual obligations
                (cardinal obligations). In such cases, liability is limited to the foreseeable,
                typically occurring damages.
              </li>
              <li>
                <strong>AI output.</strong> We expressly disclaim any warranty or liability for
                the accuracy, completeness, or suitability of AI-generated content. The User
                assumes all risk associated with reliance on AI-generated output.
              </li>
              <li>
                <strong>Liability cap.</strong> Except in cases of unlimited liability as described
                above, our total aggregate liability is capped at the total fees paid by the User
                to OXO UG in the twelve (12) months immediately preceding the event giving rise to
                the claim.
              </li>
              <li>
                <strong>Exclusion of consequential damages.</strong> To the maximum extent
                permitted by law, OXO UG shall not be liable for any indirect, incidental, special,
                consequential, or punitive damages, including but not limited to loss of profits,
                data, business opportunities, or goodwill.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">12. Termination</h2>

            <h3 className="font-medium text-base-content mb-2">Monthly subscriptions</h3>
            <p className="mb-4">
              Monthly subscriptions may be cancelled at any time. Cancellation takes effect at the
              end of the current billing period. No partial refunds are provided for unused
              portions of a billing period.
            </p>

            <h3 className="font-medium text-base-content mb-2">Annual subscriptions</h3>
            <p className="mb-4">
              Annual subscriptions automatically renew unless cancelled with at least 30 days&apos;
              notice before the renewal date. No refunds are provided for the remaining term
              after cancellation, unless required by applicable law.
            </p>

            <h3 className="font-medium text-base-content mb-2">Termination by OXO UG</h3>
            <p className="mb-4">
              We reserve the right to suspend or terminate your account immediately if you
              materially breach these Terms, engage in fraudulent activity, or use the Service in
              a manner that could harm other Users or the Service itself.
            </p>

            <h3 className="font-medium text-base-content mb-2">Data after termination</h3>
            <p>
              Upon termination, the User may export their data through the account settings prior
              to the effective termination date. All account data, including leads, campaigns, and
              AI-generated content, will be permanently deleted within 30 days following
              termination.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">13. Modifications to Terms</h2>
            <p className="mb-3">
              We may modify these Terms from time to time. For material changes, we will provide
              at least 30 days&apos; prior notice via email to the address associated with your
              account.
            </p>
            <p>
              Continued use of the Service after the effective date of any modification
              constitutes acceptance of the updated Terms. If you do not agree with the modified
              Terms, you must discontinue use of the Service and terminate your account before
              the changes take effect.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">14. Governing Law and Jurisdiction</h2>
            <p className="mb-3">
              These Terms are governed by and construed in accordance with the laws of the Federal
              Republic of Germany, excluding the UN Convention on Contracts for the International
              Sale of Goods (CISG).
            </p>
            <p>
              The exclusive place of jurisdiction for all disputes arising out of or in connection
              with these Terms is Munich, Germany, provided the User is a merchant
              (Kaufmann), a legal entity under public law, or a special fund under public law.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">15. Online Dispute Resolution</h2>
            <p className="mb-3">
              The European Commission provides an Online Dispute Resolution (ODR) platform at:{' '}
              <a
                href="https://ec.europa.eu/consumers/odr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                https://ec.europa.eu/consumers/odr
              </a>
            </p>
            <p>
              As HyPeMa is a B2B service and not directed at consumers, OXO UG is not obligated
              to and does not participate in dispute resolution proceedings before consumer
              arbitration bodies.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">16. Severability, Force Majeure, and Entire Agreement</h2>

            <h3 className="font-medium text-base-content mb-2">Severability</h3>
            <p className="mb-4">
              If any provision of these Terms is found to be invalid or unenforceable, the
              remaining provisions shall remain in full force and effect. The invalid or
              unenforceable provision shall be replaced with a valid provision that most closely
              reflects the original intent.
            </p>

            <h3 className="font-medium text-base-content mb-2">Force majeure</h3>
            <p className="mb-4">
              Neither party shall be liable for any failure or delay in performing its obligations
              under these Terms where such failure or delay results from circumstances beyond the
              reasonable control of that party, including but not limited to natural disasters,
              wars, terrorism, pandemics, government actions, power outages, internet
              disruptions, or failures of third-party service providers.
            </p>

            <h3 className="font-medium text-base-content mb-2">Entire agreement</h3>
            <p>
              These Terms, together with our{' '}
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>,
              constitute the entire agreement between you and OXO UG with respect to the use of
              the Service and supersede all prior agreements, understandings, and representations,
              whether written or oral.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">17. Contact</h2>
            <p className="mb-3">
              For questions regarding these Terms of Service, contact us at:
            </p>
            <p className="mb-1">OXO UG (haftungsbeschr&auml;nkt)</p>
            <p className="mb-1">R&uuml;benackerweg 2</p>
            <p className="mb-1">82418 Seehausen am Staffelsee, Germany</p>
            <p className="mb-1">Managing Director: Robert Stammen</p>
            <p>
              Email:{' '}
              <a href="mailto:service@newstelligent.com" className="text-primary hover:underline">
                service@newstelligent.com
              </a>
            </p>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-base-300/30 py-8 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-xs text-base-content/30">
          <span>Hy<span className="text-primary/60">Pe</span>Ma</span>
          <span>Precision over volume.</span>
        </div>
      </footer>
    </div>
  );
}
